import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal, Field } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { PendingReview } from "@/components/customer/PendingReview";

export const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NEW_FIELDS = [["full_name", "Customer name", true], ["phone", "Contact number", true], ["email", "Email address"], ["alt_phone", "Alternate contact number"],
  ["house", "House / flat no."], ["building", "Building / society"], ["locality", "Locality / service location"], ["landmark", "Landmark"],
  ["city", "City"], ["pincode", "PIN code"]];

function NewCustomer({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const nav = useNavigate();
  const submit = async (force = false) => {
    setBusy(true); setDup(null);
    try {
      const r = await api.post(`/customers${force ? "?allow_duplicate=true" : ""}`, f);
      toast.success("Customer created"); setF({}); onDone(); nav(`/customers/${r.data.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.existing) {
        setDup(d);
      } else toast.error(formatError(e));
    } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Add Customer" submitLabel="Create customer" onSubmit={() => submit()} submitting={busy} testid="new-customer-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        {NEW_FIELDS.map(([k, l, req]) => (
          <Field key={k} label={l} required={req} value={f[k]} onChange={(v) => setF((p) => ({ ...p, [k]: v }))} testid={`nc-${k}`}
            type={k === "email" ? "email" : "text"} />
        ))}
      </div>
      {dup && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="nc-duplicate">
          <p>{dup.message}: <strong>{dup.existing.full_name}</strong> ({dup.existing.customer_code})</p>
          <div className="flex gap-2 mt-2">
            <Button type="button" size="sm" variant="outline" onClick={() => nav(`/customers/${dup.existing.id}`)} data-testid="nc-open-existing">Open existing</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => submit(true)} data-testid="nc-create-anyway">Create anyway</Button>
          </div>
        </div>)}
      <p className="text-xs text-muted-foreground">Requirements, bookings and payments can be added from the customer profile after creation. Existing bookings and leads with the same phone number are linked automatically.</p>
    </Modal>
  );
}

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const nav = useNavigate();
  const { can, user } = useAuth();
  const load = () => api.get("/customers", { params: { q: q || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);
  const sync = async () => {
    setSyncing(true);
    try { const r = await api.post("/customers/sync"); toast.success(`Synced: ${r.data.created} new customers, ${r.data.linked} links`); load(); }
    catch (e) { toast.error(formatError(e)); } finally { setSyncing(false); }
  };
  const columns = [
    { key: "full_name", header: "Customer", render: (r) => <div><p className="font-semibold">{r.full_name}</p><p className="text-xs text-muted-foreground">{r.customer_code}</p></div> },
    { key: "phone", header: "Phone" }, { key: "email", header: "Email" },
    { key: "city", header: "Location", render: (r) => [r.locality, r.city].filter(Boolean).join(", ") || "—" },
    { key: "bookings", header: "Bookings", render: (r) => <span data-testid={`cust-bookings-${r.id}`}>{r.booking_count}{r.lead_count ? <span className="text-xs text-muted-foreground"> · {r.lead_count} lead{r.lead_count > 1 ? "s" : ""}</span> : null}</span> },
    { key: "inv", header: "Total payable", render: (r) => inr(r.balance?.total_payable) },
    { key: "due", header: "Outstanding", render: (r) => <span className="font-semibold" data-testid={`cust-due-${r.id}`}>{r.balance?.overpaid_amount ? `Excess ${inr(r.balance.overpaid_amount)}` : inr(r.balance?.remaining_amount)}</span> },
    { key: "ps", header: "Payment", render: (r) => <StatusBadge status={r.balance?.payment_status} /> },
  ];
  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle={`${rows.length} customers · Customer 360° directory`} actions={can("customers:manage") && (<>
        <Button variant="outline" onClick={sync} disabled={syncing} data-testid="customers-sync-btn"><ArrowsClockwise size={16} className={syncing ? "animate-spin mr-1" : "mr-1"} />Sync from Bookings & Leads</Button>
        <Button onClick={() => setOpen(true)} className="bg-gold text-black font-semibold shadow-md hover:-translate-y-0.5 transition-transform" data-testid="customers-new-btn"><Plus size={16} weight="bold" className="mr-1" />Add Customer</Button>
      </>)} />
      {user?.role === "super_admin" && <PendingReview onDone={load} />}
      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone or booking reference" className="pl-9 bg-white" data-testid="customers-search" />
      </div>
      <DataTable columns={columns} rows={rows} onRowClick={(r) => nav(`/customers/${r.id}`)} empty="No customers yet. Click “+ Add Customer” or “Sync from Bookings & Leads”." />
      <NewCustomer open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
